import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ReportData } from "../types";
import { ascii, C, MARGIN, PdfWriter } from "./pdfParts";
import {
  MONTH_STATUS_LABEL,
  PARTNER_SETUP_INCOMPLETE,
  partnerSection,
  reconciliationRows,
  reportFileName,
  reportTitle,
} from "./reportSections";

/**
 * Financial Report PDF (A4 portrait) for any period. Draws the normalized
 * ReportData exactly as the screen and print layout show it — no totals are
 * computed here. Order: header, summary boxes + equation, Detailed Income,
 * Detailed Expenses, Reconciliation, Partner / Mortgage Share, footer.
 * Fits one page for a normal month; longer months flow onto more pages.
 */
export const buildReportPdf = (report: ReportData, generatedAt: Date = new Date()): jsPDF => {
  const w = new PdfWriter();
  const { doc } = w;

  // --- Header ---------------------------------------------------------------
  w.centered(report.project.name, 16, C.text, true, 6);
  w.centered(reportTitle(report), 11, C.slate);
  const statusNote = report.monthly ? `   |   Status: ${MONTH_STATUS_LABEL[report.monthly.month.status]}` : "";
  w.centered(`${report.period.label}${statusNote}`, 9.5, C.muted);

  // --- Summary boxes, equation, income and expense tables --------------------
  w.summaryBoxes(report);
  w.incomeAndExpenseTables(report);

  // --- Reconciliation (every period) ------------------------------------------
  const rec = reconciliationRows(report);
  w.sectionTitle("Reconciliation", 14);
  const statusColor = rec.statusKey === "reconciled" ? C.green : rec.statusKey === "needs_review" ? C.red : C.muted;
  autoTable(doc, {
    ...w.tableBase,
    startY: w.y,
    head: [["Calculated Balance", "Actual Balance", "Difference", "Status"]],
    body: [[rec.calculated, rec.actual, rec.difference, { content: rec.status, styles: { textColor: statusColor, fontStyle: "bold" } }]],
    columnStyles: {},
    headStyles: { fillColor: C.headBg, textColor: C.text, fontStyle: "bold" },
  });
  w.y = w.lastTableEnd() + (rec.statusKey === "monthly_only" ? 4 : 7);
  if (rec.statusKey === "monthly_only" && rec.note) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    doc.text(ascii(rec.note), MARGIN, w.y);
    w.y += 7;
  }

  // --- Partner / Mortgage Share (monthly, 2+ partners) ------------------------
  const partners = partnerSection(report);
  if (partners.state !== "not_applicable") {
    w.sectionTitle("Partner Contribution / Mortgage Share", partners.state === "ready" ? 60 : 16);
    if (partners.state === "incomplete") {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...C.red);
      doc.text(PARTNER_SETUP_INCOMPLETE, MARGIN, w.y + 4);
      doc.setFont("helvetica", "normal");
      partners.missing.forEach((m, i) => doc.text(ascii(`- ${m}`), MARGIN + 3, w.y + 9 + i * 4.5));
      w.y += 10 + partners.missing.length * 4.5;
    } else {
      const table = partners.table;
      const partnerCols: Record<number, object> = {};
      table.headers.slice(1).forEach((_, i) => (partnerCols[i + 1] = { halign: "right" }));
      autoTable(doc, {
        ...w.tableBase,
        startY: w.y,
        head: [table.headers.map((h, i) => (i === 0 ? h : { content: ascii(h), styles: { halign: "right" } }))],
        body: table.rows.map((r) =>
          [r.label, ...r.cells].map((c) => (r.emphasis ? { content: c, styles: { fontStyle: "bold", fillColor: C.greenBg } } : c))
        ),
        columnStyles: partnerCols,
        headStyles: { fillColor: C.slateBg, textColor: C.text, fontStyle: "bold" },
      });
      w.y = w.lastTableEnd() + 4;

      // Highlighted payment box — the split must be impossible to miss.
      const boxHeight = 17;
      w.ensureSpace(boxHeight + 2);
      doc.setFillColor(...C.amberBg);
      doc.setDrawColor(...C.amber);
      doc.setLineWidth(0.4);
      doc.roundedRect(MARGIN, w.y, w.contentW, boxHeight, 2, 2, "FD");
      const cells = [{ name: "Monthly Mortgage", amount: table.mortgage }, ...table.payments.map((p) => ({ name: `${p.name} to Pay`, amount: p.amount }))];
      const cellW = w.contentW / cells.length;
      cells.forEach((c, i) => {
        const cx = MARGIN + cellW * i + cellW / 2;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...C.muted);
        doc.text(ascii(c.name), cx, w.y + 6, { align: "center" });
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(...(i === 0 ? C.slate : C.text));
        doc.text(c.amount, cx, w.y + 13, { align: "center" });
      });
      w.y += boxHeight + 4;
    }
  }

  w.footers(`${report.project.name} - ${report.period.label}`, generatedAt);
  return doc;
};

/** Download the full financial report (never the transaction ledger) as a PDF. */
export const downloadReportPdf = (report: ReportData) => {
  buildReportPdf(report).save(reportFileName(report));
};
